/**
 * A dated list of things that happened — "10:42 · Campaign started · 1,204
 * queued". The concept's membership log (`.sb-log`, `.sb-event`), and the
 * shape the Sense actions ledger, the admin audit trail and an interest's
 * timeline all take.
 *
 * ## WHY A LIST AND NOT A TABLE
 *
 * Those three screens drew their history as tables, which makes every event
 * the same width of fact: a "who" column that is empty for system events, a
 * "detail" column that is empty for most rows. A log is read top to bottom as
 * a story, and an `<ol>` is what a story is: a screen reader hears "list, 14
 * items" and the order is part of the meaning. Each item is a four-column grid
 * — when, an icon tile, the title with its detail under it, and an aside for a
 * count — and every fact a table column held survives as one of those fields.
 *
 * ## TONE COLOURS THE ICON, AND ONLY THE ICON
 *
 * The title is always `text-text` and the time always muted. A tone tints the
 * small icon tile, so the eye can find "the failed one" without the row
 * shouting. A status tone (`good`, `warn`, `bad`) is allowed only when the
 * event *is* that status — a send that failed is `bad`; "Campaign started" is
 * not `good` just because nothing went wrong. Sources use identity tones.
 * Without an icon the tile holds a small tone dot.
 *
 * ## ROWS ARE TOP-ALIGNED, ON THE ICON TILE'S 22PX LINE
 *
 * The time, the tile and the aside sit on the first 22px line and the title
 * block is at least that tall, centred in it. A one-line event therefore reads
 * exactly as a centred row would; a tall one — an audit entry listing four
 * changed fields — keeps its time and icon beside its title rather than
 * floating halfway down the detail, which is how the first cut drew them
 * (`items-center`) and why the admin audit trail looked loose.
 *
 * ## THE TIME COLUMN FITS WHAT THE CALLER PUTS IN IT
 *
 * The concept's log showed clock times ("09:41") and its column was 62px.
 * Every product consumer passes a date ("06 Oct 2026"), which does not fit in
 * 62px of 0.68rem mono: the Sense ledger wrapped it onto two lines and the
 * admin timelines onto three, a ragged rail with every row taller than its
 * content (slice 66 review). So `whenWidth` is `'time'` (62px, the concept's)
 * or `'date'` (88px), and a caller showing a date and a time puts them on two
 * `whitespace-nowrap` lines, never three.
 *
 * ## NOTHING IS CUT OFF
 *
 * The title wraps; it is never truncated. Tooltips are banned here (R10), so a
 * clipped title could not be recovered by anyone, and at 390px with an
 * e-mail address in the aside the first build cut "Feature request" to
 * "Fea…". Below `sm` the aside moves to its own line under the title and may
 * break anywhere — an address has no spaces to break at — and from `sm` it
 * takes its right-hand column again.
 *
 * ## SCROLLING
 *
 * With `maxHeight` the list scrolls, and a scrolling region a keyboard user
 * cannot focus is one they cannot read past the fold. So a scrolling log gets
 * `tabIndex={0}` and the focus ring; a log that does not scroll gets neither,
 * because a focus stop on something with nothing to do is a trap of its own.
 * The visible label names the list through `aria-labelledby`.
 *
 * ## THE HOLE IT LEAVES
 *
 * It does not know about time zones. `when` is a node the caller formats, and
 * the caller decides whether 10:42 is the workspace's time or the viewer's.
 */
import { useId, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import type { IdentTone } from '../lib/identity';
import { DOT_CLASS, ICON_CLASS } from '../lib/tone-classes';
import { Kicker } from './Kicker';

export type EventLogTone = 'neutral' | 'brand' | 'good' | 'warn' | 'bad' | IdentTone;

export interface EventLogItem {
  readonly key: string;
  /** The time, already formatted — "10:42", "17 Oct". Keep each line `whitespace-nowrap`. */
  readonly when: ReactNode;
  /** Colours the icon tile only. Default `'neutral'`. */
  readonly tone?: EventLogTone;
  readonly icon?: ReactNode;
  readonly title: ReactNode;
  readonly detail?: ReactNode;
  /** Right-hand slot — a count, a delta. */
  readonly aside?: ReactNode;
}

export interface EventLogProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The visible kicker, and the list's accessible name. */
  label: string;
  items: readonly EventLogItem[];
  /** In px. When set, the list scrolls and becomes focusable. */
  maxHeight?: number;
  /** Right-hand slot in the head — running totals. */
  totals?: ReactNode;
  /** How wide the time column is: `'time'` (default) for "09:41", `'date'` for "06 Oct 2026". */
  whenWidth?: 'time' | 'date';
}

const WHEN_COLUMNS = {
  time: 'grid-cols-[52px_minmax(0,1fr)] sm:grid-cols-[62px_22px_minmax(0,1fr)_auto]',
  date: 'grid-cols-[76px_minmax(0,1fr)] sm:grid-cols-[88px_22px_minmax(0,1fr)_auto]',
} as const;

export function EventLog({ label, items, maxHeight, totals, whenWidth = 'time', className, ...rest }: EventLogProps) {
  const labelId = useId();
  const scrolls = maxHeight !== undefined;

  return (
    <div className={cn('rounded-base border border-line bg-black/20 px-3.5 py-3', className)} {...rest}>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <Kicker id={labelId}>{label}</Kicker>
        {totals !== undefined && totals !== null && (
          <span className="text-[0.74rem] tabular-nums text-muted">{totals}</span>
        )}
      </div>
      <ol
        aria-labelledby={labelId}
        tabIndex={scrolls ? 0 : undefined}
        style={scrolls ? { maxHeight } : undefined}
        className={cn(scrolls && cn('overflow-y-auto overscroll-contain rounded-xs', focusRing))}
      >
        {items.map((item) => {
          const tone = item.tone ?? 'neutral';
          return (
            <li
              key={item.key}
              className={cn(
                'grid items-start gap-x-2 gap-y-1 border-t border-line py-2 text-[0.8rem] first:border-t-0',
                WHEN_COLUMNS[whenWidth],
              )}
            >
              <span className="font-mono text-[0.68rem] leading-[22px] text-muted">{item.when}</span>
              <span
                aria-hidden="true"
                className={cn(
                  "hidden size-[22px] place-items-center rounded-xs bg-white/[0.08] sm:grid [&_svg:not([class*='size-'])]:size-3.5",
                  ICON_CLASS[tone],
                )}
              >
                {item.icon ?? <span className={cn('size-1.5 rounded-full', DOT_CLASS[tone])} />}
              </span>
              <span className="flex min-h-[22px] min-w-0 flex-col justify-center">
                <span className="break-words text-text">{item.title}</span>
                {item.detail !== undefined && item.detail !== null && (
                  <span className="text-[0.72rem] text-muted">{item.detail}</span>
                )}
              </span>
              {item.aside !== undefined && item.aside !== null && (
                <span className="col-start-2 min-w-0 break-all text-[0.74rem] tabular-nums text-muted sm:col-start-auto sm:break-normal sm:whitespace-nowrap sm:text-right sm:leading-[22px]">
                  {item.aside}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
